import { resolve, join, dirname } from 'node:path';
import { homedir } from 'node:os';
import { existsSync, mkdirSync, writeFileSync, readFileSync, realpathSync } from 'node:fs';
import { createConnection } from 'node:net';
import { fileURLToPath } from 'node:url';
import { ProxyServer } from './proxy/server.js';
import { LocalToolsHandler } from './proxy/local-tools.js';
import { createWebServer } from './web/server.js';
import { loadConfig, getDefaults, type CliOverrides } from './config.js';
import { LicenseService } from './licensing/service.js';
import { planContext } from './billing/context.js';
import { RecordingEngine } from './recording/engine.js';
import { StreamingServer } from './streaming/server.js';
import { HubStreamingServer } from './streaming/hub.js';
import { StreamRelay } from './streaming/relay.js';

export interface BrowserForgeCliConfig {
  port: number;
  target: string;
  storageDir: string;
  chromePort: number;
  maxSessions: number;
  maxAgeDays: number;
  webOnly: boolean;
  help: boolean;
  version: boolean;
}

function parseArgs(argv: string[]): BrowserForgeCliConfig {
  const defaults = getDefaults();
  const config: BrowserForgeCliConfig = {
    port: defaults.ui.port,
    target: 'npx chrome-devtools-mcp',
    storageDir: defaults.storage.dir,
    chromePort: defaults.chrome.debugPort,
    maxSessions: defaults.storage.maxSessions,
    maxAgeDays: defaults.storage.maxAgeDays,
    webOnly: false,
    help: false,
    version: false,
  };

  for (let i = 2; i < argv.length; i++) {
    const arg = argv[i];
    const next = argv[i + 1];

    switch (arg) {
      case '--port':
        config.port = parseInt(next, 10);
        i++;
        break;
      case '--target':
        config.target = next;
        i++;
        break;
      case '--storage-dir':
        config.storageDir = resolve(next);
        i++;
        break;
      case '--chrome-port':
        config.chromePort = parseInt(next, 10);
        i++;
        break;
      case '--max-sessions':
        config.maxSessions = parseInt(next, 10);
        i++;
        break;
      case '--max-age-days':
        config.maxAgeDays = parseInt(next, 10);
        i++;
        break;
      case '--web-only':
        config.webOnly = true;
        break;
      case '--help':
      case '-h':
        config.help = true;
        break;
      case '--version':
      case '-v':
        config.version = true;
        break;
    }
  }

  return config;
}

/** Read version from package.json at runtime. */
function getVersion(): string {
  const currentDir = dirname(fileURLToPath(import.meta.url));
  // dist/cli.js → look for package.json in parent; src/index.ts → also parent
  const pkgPath = join(currentDir, '..', 'package.json');
  try {
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8')) as { version: string };
    return pkg.version;
  } catch {
    return '0.0.0';
  }
}

function printHelp(): void {
  console.log(`
BrowserForge v${getVersion()} — Browser session recorder for AI agents

Usage: browserforge [options]

Options:
  --port <number>         Web UI port (default: 7600)
  --target <command>      Target MCP server command (default: 'npx chrome-devtools-mcp')
  --storage-dir <path>    Session storage directory (default: ~/.browserforge/sessions)
  --chrome-port <number>  Chrome CDP debug port (default: 9222)
  --max-sessions <number> Max sessions to keep (default: 100)
  --max-age-days <number> Max session age in days (default: 30)
  --web-only              Run only the web UI server (no MCP proxy)
  --help, -h              Show this help
`);
}

/** Ensure ~/.browserforge exists with a default config.json on first run. */
function ensureFirstRun(bfDir: string): void {
  if (!existsSync(bfDir)) {
    mkdirSync(bfDir, { recursive: true });
  }

  const configPath = join(bfDir, 'config.json');
  if (!existsSync(configPath)) {
    const defaults = getDefaults();
    const defaultConfig = {
      storage: {
        maxSessions: defaults.storage.maxSessions,
        maxAgeDays: defaults.storage.maxAgeDays,
      },
      ui: { port: defaults.ui.port },
      screencast: {
        enabled: defaults.screencast.enabled,
        quality: defaults.screencast.quality,
        maxFps: defaults.screencast.maxFps,
      },
      chrome: { debugPort: defaults.chrome.debugPort },
    };
    writeFileSync(configPath, JSON.stringify(defaultConfig, null, 2) + '\n');
  }
}

/** Resolve the dist/ui static directory relative to this file. */
function resolveStaticDir(): string {
  // In production (dist/cli.js), __dirname is dist/
  // In dev (src/index.ts via tsx), __dirname is src/
  const currentDir = dirname(fileURLToPath(import.meta.url));
  const candidates = [
    join(currentDir, 'ui'),           // dist/ui (production)
    join(currentDir, '..', 'dist', 'ui'), // src/../dist/ui (dev)
  ];
  for (const candidate of candidates) {
    if (existsSync(candidate)) return candidate;
  }
  // Fallback — may not exist yet
  return join(currentDir, 'ui');
}

/** Check if a TCP port is already in use (i.e. the hub is running). */
function probePort(port: number, host: string): Promise<boolean> {
  return new Promise((resolve) => {
    const socket = createConnection({ port, host });
    socket.setTimeout(500);
    socket.on('connect', () => {
      socket.destroy();
      resolve(true);
    });
    socket.on('error', () => {
      socket.destroy();
      resolve(false);
    });
    socket.on('timeout', () => {
      socket.destroy();
      resolve(false);
    });
  });
}

async function main(): Promise<void> {
  const cliConfig = parseArgs(process.argv);

  if (cliConfig.version) {
    console.log(getVersion());
    process.exit(0);
  }

  if (cliConfig.help) {
    printHelp();
    process.exit(0);
  }

  const bfDir = resolve(homedir(), '.browserforge');
  ensureFirstRun(bfDir);

  // Load license and set plan context
  const licenseService = new LicenseService(bfDir);
  const licenseStatus = licenseService.getStatus();
  if (licenseStatus.active) {
    planContext.setPlan(licenseStatus.plan);
  }

  // Load config: defaults < file config < CLI overrides
  const overrides: CliOverrides = {
    port: cliConfig.port,
    storageDir: cliConfig.storageDir,
    chromePort: cliConfig.chromePort,
  };
  const config = loadConfig(join(bfDir, 'config.json'), overrides);

  // Apply max-sessions / max-age-days from CLI if provided
  if (process.argv.includes('--max-sessions')) {
    config.storage.maxSessions = cliConfig.maxSessions;
  }
  if (process.argv.includes('--max-age-days')) {
    config.storage.maxAgeDays = cliConfig.maxAgeDays;
  }

  // Ensure storage dir exists
  mkdirSync(config.storage.dir, { recursive: true });

  // Welcome banner
  const liveUrl = `http://localhost:${config.ui.port}`;
  const version = getVersion();
  const planLabel = licenseStatus.active
    ? `BrowserForge ${licenseStatus.plan.charAt(0).toUpperCase() + licenseStatus.plan.slice(1)} activated`
    : 'BrowserForge Free';
  console.error(`
╔═══════════════════════════════════════╗
║         BrowserForge v${version.padEnd(16)}║
╚═══════════════════════════════════════╝
  Plan:        ${planLabel}
  Live viewer: ${liveUrl}
  Target:      ${cliConfig.target}
  Storage:     ${config.storage.dir}
  Chrome CDP:  port ${config.chrome.debugPort}
`);

  // Start the web server
  const staticDir = resolveStaticDir();
  const webServer = createWebServer({
    port: config.ui.port,
    host: config.ui.host,
    storageDir: config.storage.dir,
    staticDir,
    licenseService,
  });
  await webServer.listen();

  if (cliConfig.webOnly) {
    // Web-only mode: serve UI + API + hub streaming (no MCP proxy)
    const hub = new HubStreamingServer({
      server: webServer.server,
      maxFps: config.screencast.maxFps,
    });
    console.error('[browserforge] Running in web-only mode with live relay hub');

    const shutdown = async () => {
      console.error('[browserforge] shutting down...');
      hub.shutdown();
      await webServer.close();
      process.exit(0);
    };

    process.on('SIGINT', shutdown);
    process.on('SIGTERM', shutdown);
  } else {
    // Full mode: MCP proxy + recording engine + streaming + web UI
    const localTools = new LocalToolsHandler({
      storageDir: config.storage.dir,
      liveUrl,
    });
    const proxy = new ProxyServer({ targetCommand: cliConfig.target, localTools });

    // Recording engine — captures screenshots via CDP on every capturable tool call
    const engine = new RecordingEngine({ config });
    await engine.start();

    // Check if the hub (web-only instance) is already running on the UI port.
    // If so, skip starting the local web server and just relay to the hub.
    const hubRunning = await probePort(config.ui.port, config.ui.host);

    // Local streaming server — broadcasts to any locally connected UI clients
    const streaming = new StreamingServer({
      server: webServer.server,
      engine,
      maxFps: config.screencast.maxFps,
    });

    // Forward screencast frames from CDP → streaming server → web UI
    engine.on('screencast-frame', (frame: Buffer) => {
      streaming.broadcastFrame(frame);
      streaming.setLatestFrame(frame);
    });

    // Relay events to the hub if it's running (or will be running later — relay auto-reconnects)
    const relay = new StreamRelay({
      engine,
      hubUrl: `ws://${config.ui.host}:${config.ui.port}/ws/ingest`,
    });

    if (hubRunning) {
      console.error(`[browserforge] Hub detected on port ${config.ui.port}, relaying events`);
    }

    // Wire proxy tool calls → recording engine
    proxy.onToolCall((event) => {
      console.error(
        `[browserforge] tool-call: ${event.tool} (${event.durationMs}ms)`,
      );
      engine.handleToolCall(event).catch((err) => {
        console.error(
          `[browserforge] recording error: ${err instanceof Error ? err.message : String(err)}`,
        );
      });
    });

    // Forward proxy output to our stdout (back to the agent)
    proxy.onOutput((line) => {
      process.stdout.write(line + '\n');
    });

    await proxy.start();

    // Read from our stdin and forward to the proxy
    const { createInterface } = await import('node:readline');
    const rl = createInterface({ input: process.stdin });
    rl.on('line', (line) => {
      try {
        const msg = JSON.parse(line) as Record<string, unknown>;
        proxy.send(msg);
      } catch {
        // Invalid JSON from agent — forward raw
        if (proxy.isRunning) {
          proxy.send({ raw: line } as Record<string, unknown>);
        }
      }
    });

    // Graceful shutdown
    const shutdown = async () => {
      console.error('[browserforge] shutting down...');
      rl.close();
      relay.shutdown();
      streaming.shutdown();
      await engine.shutdown();
      await Promise.all([proxy.close(), webServer.close()]);
      process.exit(0);
    };

    process.on('SIGINT', shutdown);
    process.on('SIGTERM', shutdown);

    rl.on('close', () => {
      // stdin closed — agent disconnected
      relay.shutdown();
      streaming.shutdown();
      void engine.shutdown().then(() =>
        Promise.all([proxy.close(), webServer.close()]).then(() =>
          process.exit(0),
        ),
      );
    });
  }
}

// Only run main when executed directly (not imported).
// Use realpathSync to resolve .bin/ symlinks before checking the filename.
const isDirectExecution = (() => {
  try {
    const real = realpathSync(process.argv[1] ?? '');
    return (
      real.endsWith('/cli.js') ||
      real.endsWith('/index.ts') ||
      real.endsWith('/src/index.ts')
    );
  } catch {
    return false;
  }
})();

if (isDirectExecution) {
  main().catch((err) => {
    console.error('[browserforge] Fatal error:', err);
    process.exit(1);
  });
}

export { parseArgs };
